import type {
    AiGuideMathExpressionsRenderedV2,
    ContentLibraryMathExpressionsRenderedV2,
} from "@khanacademy/event-schemas";
import {logUnreachableCode} from "@khan/logging";

export type Event =
    | AiGuideMathExpressionsRenderedV2
    | ContentLibraryMathExpressionsRenderedV2;

type EventContext = "ai-guide" | "content-library";

export interface EventParams {
    context?: EventContext;
    currentPageURL?: string;
    /**
     * The math renderer that was used for the expressions related to this
     * event. You probably want to set this to "mathjax3"; MathJax 2 and
     * KaTeX were our old math renderers, and were removed from webapp in March
     * 2025.
     */
    renderer?: "katex" | "mathjax2" | "mathjax3";
    numExpressions?: number;
}

export function createEvent(params: EventParams): Event {
    const context = params.context ?? "content-library";
    const currentPageURL = params.currentPageURL ?? "";
    const renderer = params.renderer ?? "katex";
    const numExpressions = params.numExpressions ?? 1;
    switch (context) {
        case "ai-guide":
            return {
                eventType: "AiGuideMathExpressionsRendered",
                eventSchemaVersion: 2,
                currentPageURL,
                renderer,
                numExpressions,
            };
        case "content-library":
            return {
                eventType: "ContentLibraryMathExpressionsRendered",
                eventSchemaVersion: 2,
                currentPageURL,
                renderer,
                numExpressions,
            };
        default:
            throw logUnreachableCode("unexpected TeX event context", context);
    }
}

// Merge two events into one, summing their numExpressions. Assumes the events
// have the same values for all other fields.
export function mergeEvents(a: Event, b: Event): Event {
    return {...a, numExpressions: a.numExpressions + b.numExpressions};
}

export function multiply(x: number, event: Event): Event {
    return {...event, numExpressions: x * event.numExpressions};
}
